import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';

import { ApplicationState } from '../../store';
import * as CustomerStateController from '../../store/StateControllers/Customers/CustomersStateController';
import { ScrollableContent, TopActionsAndScrollableContentContainer, TopActionButtons } from '../core/TopActionsAndScrollableContentContainer';
import { CustomerRequest } from '../../store/Models/Customers/Customer';
import { MyCustomersProps } from './MyCustomersCommon';
import { TableColumn, TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';

import { SharedMaterialCoreData } from '../../store/Models/SharedMaterials/DataModel';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import { Link } from 'react-router-dom';

import './MyCustomers.css';
import { SelectedInventoriesManager } from '../../store/Managers/SelectedInventoriesManager';
import { ProgressBar } from '../core/ProgressBar';
import { ReportTypesManager } from '../../store/Managers/ReportTypesManager';
import { OfflineProcessPollingManager } from '../../store/Managers/OfflineProcessPollingManager';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';

type MyCustomerRequestDetailProps =
    MyCustomersProps &
    RouteComponentProps<{ customerId: string, requestId: string }>;

class MyCustomerRequestDetail extends ToxnotSignInRequiredComponent<MyCustomerRequestDetailProps> {

    // ===============================================================================

    public componentDidMount() {
        const inventoryIdsSelected = SelectedInventoriesManager.getThenClearSelectedInventorieFromLocalStorage(this.getUserContextForSignedInUser().userId);

        if (inventoryIdsSelected !== null && inventoryIdsSelected.length > 0) {
            this.props.addInventoriesToReport(this.props.match.params.requestId, inventoryIdsSelected);
        }

        const request = CustomerStateController.getRequestFromState(this.props, this.props.match.params.requestId);

        if (request.requestData.passportIdsContainingReports.length > 0) {
            this.props.getSharedMaterialsFromIds(request.requestData.passportIdsContainingReports);
        }

    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public renderConcreteComponent() {
        const request = CustomerStateController.getRequestFromState(this.props, this.props.match.params.requestId);

        return (
            <div id="customer-request-detail-root">
                {this.renderMainContent(request)}
            </div>
        )
    }

    // ===============================================================================

    private renderMainContent(request: CustomerRequest) {
        let body: JSX.Element | null = null;

        const state = this.props.requestsResponding[this.props.match.params.requestId]

        if (state) {
            body = (
                <div className="tn-section-row">
                    <div className="tn-section-col-12">
                        <div className="alert alert-info">
                            <p>
                                {this.localizeString("Creating reports and publishing passports")}
                            </p>
                            <p>
                                {this.localizeString("You can safely leave this page")}
                            </p>
                        </div>
                        <ProgressBar
                            currentProgressIndex={state.currentProgressItemIndex}
                            totalNumItems={state.progressTotalNumItems}
                            localizer={this.props.appServices}
                            localizedTitle={OfflineProcessPollingManager.getFriendlyNameForOfflineProcessStage(state.processingStage, this.props.appServices)} />
                    </div>
                </div>
            );
        }

        else {
            body = (
                <React.Fragment>
                    {this.renderTopActionButtons(request)}
                    {this.renderRequestInfoSection(request)}
                </React.Fragment>
            )
        }

        return body;
    }

    // ===============================================================================

    private renderTopActionButtons(request: CustomerRequest) {
        const buttonText = request.dateResponded === null ? this.localizeString("Respond to Request") : this.localizeString("Re-Respond to Request")
        return (
            <div className="tn-section-row">
                <div className="tn-section-col-12 request-action-buttons-container">
                    <p>
                        <button id="respond-to-request" className="btn btn-toxnot-primary" onClick={e => this.respondToRequest_click(request)}>{buttonText}</button>
                    </p>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderRequestInfoSection(request: CustomerRequest) {
        const reportTypesAndRegulations = this.getUserContextForSignedInUser().reportTypesAndRegulations;

        const reportTypeFriendlyDescriptionsToUse = CustomerStateController.getReportTypesAsDisplayableList(request, this.props.appServices, reportTypesAndRegulations.dynRegsForUser).sort();

        return (
            <div id="customer-request-detail-request-info-container">
                <div>
                    <div>
                        <div className="grouped-data">
                            <div className="group">
                                <div className="title">{this.localizeString("Requested Data")}</div>
                                <div className="content">
                                    <p>
                                        {this.localizeString("Requested By: ") + request.requestData.requestContactName + `<${request.requestData.requestContactEmail}>`}
                                    </p>
                                    <p>
                                        {this.localizeString("Concerning Factory: ") + request.requestData.factoryName.emptyCoalesce(this.localizeString("N/A"))}
                                    </p>
                                    <p>
                                        {this.localizeString("Requested on: ") + new Date(request.dateCreated).toLocaleDateString()}
                                    </p>
                                    <p>
                                        {this.localizeString("Report Types:")}
                                    </p>
                                    <ul>
                                        {reportTypeFriendlyDescriptionsToUse.map((r, i) => <li key={i}>{r}</li>)}
                                    </ul>
                                    <hr />
                                    <div className="request-detail-data-container">
                                        <TopActionsAndScrollableContentContainer shouldMakeTnSection={false}>
                                            <TopActionButtons>
                                                <a role="button" id="add-materials-from-lib-button" className="btn btn-default" onClick={e => SelectedInventoriesManager.navigateToSelectInventoriesPage(this.getUserContextForSignedInUser().userId, this.localizeString("Add Materials to Report"))}>{this.localizeString("Add Parts from Library")}</a>
                                            </TopActionButtons>
                                            <ScrollableContent>
                                                <p>
                                                    {request.requestData.inventoriesToReport.length === 0 && !request.requestData.openRequest && this.localizeString("You must add at least one material before responding to a request.")}
                                                </p>
                                                <p>
                                                    {request.requestData.inventoriesToReport.length === 0 && request.requestData.openRequest && this.localizeString("Your customer wants a report on every material you sell them.")}
                                                </p>
                                                <label>{this.localizeString("Requested Parts")}</label>
                                                {request.requestData.inventoriesToReport.map((inv, index) => (
                                                    <div key={index} className="inventory-to-report-container">
                                                        <div className="item">
                                                            <div className="inventory-to-report-name">
                                                                {inv.inventoryId !== null ?
                                                                    <a href={`/Data/Inventory?inventoryId=${inv.inventoryId}`} target="_blank">{inv.name}</a> :
                                                                    <span>{inv.name}</span>
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className="item">
                                                            <button className="btn btn-sm btn-toxnot-danger" onClick={e => this.removeInventoryToReport_click(index)}><span className="glyphicon glyphicon-remove"></span></button>
                                                        </div>
                                                        {inv.inventoryId === null && <div className="item">
                                                            {this.localizeString("Warning! No matching material was found for this part name.")}
                                                        </div>}
                                                    </div>
                                                ))}
                                            </ScrollableContent>
                                        </TopActionsAndScrollableContentContainer>
                                    </div>
                                </div>
                            </div>
                            {request.dateResponded !== null &&
                                <div className="group">
                                    <div className="title">{this.localizeString("Response Data")}</div>
                                    <div className="content">
                                        <p>
                                            {request.dateResponded !== null && this.localizeString("Date Responded: ") + new Date(request.dateResponded).toLocaleDateString()}
                                        </p>
                                        {request.requestData.combinedReportFileId &&
                                            <p>
                                                <a href={`/Api/v2/GeneralFile?fileId=${encodeURIComponent(request.requestData.combinedReportFileId)}`}>{this.localizeString("Download all reports")}</a>
                                            </p>
                                        }
                                        <div className="request-detail-data-container">
                                            <TopActionsAndScrollableContentContainer shouldMakeTnSection={false}>
                                                <ScrollableContent>
                                                    <ToxnotReactTable
                                                        localizer={this.props.appServices}
                                                        allRowModels={Object.values(this.props.sharedMaterialIdsToCoreDataTable)}
                                                        headerRow={this.getHeaderForForReportedPassports()}
                                                        getRowForTable={this.getRowForReportedMaterials}
                                                    />
                                                </ ScrollableContent>
                                            </ TopActionsAndScrollableContentContainer>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                    <div>
                        <div className="grouped-data">
                            <div className="group">
                                <div className="title">{this.localizeString("Notes")}</div>
                                <div className="content">
                                    <textarea className="form-control" rows={6} style={{ resize: "none" }} onChange={e => this.requestNotes_onChange(e, request)} defaultValue={request.requestData.notes} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ===============================================================================

    private getHeaderForForReportedPassports() {
        return [
            {
                value: this.localizeString("Name"),
                isSortable: true,
                getSortableValue: (s: SharedMaterialCoreData) => s.materialName
            }
        ] as TableColumn<SharedMaterialCoreData>[];
    }

    // ===============================================================================

    private getRowForReportedMaterials = (sharedMaterial: SharedMaterialCoreData, index: number) => {
        return {
            cols: [<Link to={ToxnotReactComponentPaths.SharedMaterialBaseDetailPath + "/" + sharedMaterial.sharedMaterialId}>{sharedMaterial.materialName}</Link>]
        } as TableRow
    }

    // ===============================================================================
    //
    // UI Events
    //

    private requestNotes_onChange = (e: React.ChangeEvent<HTMLTextAreaElement>, request: CustomerRequest) => {
        this.props.updateRequestNotes(e.currentTarget.value, request);
    }

    // ===============================================================================

    private respondToRequest_click = (request: CustomerRequest) => {
        if (request.requestData.inventoriesToReport.length === 0) {
            alert(this.localizeString("You must pick at least one inventory to report on."));
            return;
        }

        if (request.requestData.inventoriesToReport.some(i => i.inventoryId === null)) {
            if (!confirm(this.localizeString("Some of the materials requested do not match any of your materials. This will be excluded from the generated reports."))) {
                return;
            }
        }

        this.props.setRequestIdForModal(this.props.match.params.requestId);

        if (ReportTypesManager.doesCustomerRequestRequireList(request)) {
            $('#select-list-modal').modal('show');
            return;
        }

        this.props.respondToRequest(request.requestId, true);
    }

    // ===============================================================================

    private removeInventoryToReport_click = (index: number) => {
        if (!confirm(this.localizeString("Removing this inventory will remove it from the report. Are you sure you want to continue?"))) {
            return;
        }

        this.props.removeInventoryToReport(index, this.props.match.params.requestId);
    }
}

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(MyCustomerRequestDetail as any);
